import {
  PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS,
  PROCORE_DAILY_LOG_CATEGORY_ORDER,
  type ProcoreDailyLogCategory,
} from "./procoreDailyLog";
import {
  renderTradeVocabularyContract,
  type TradeVocabularyTarget,
} from "./tradeVocabulary";

export interface AutodeskTemplateFieldSnapshot {
  uid: string;
  label: string;
  type: "question" | "table" | "unknown";
  valueName?: string;
  schema?: string;
  required: boolean;
  presets: string[];
}

export interface AutodeskTemplateColumnSnapshot {
  uid: string;
  label: string;
  columnKey?: string;
  columnType?: string;
  presets: string[];
}

export type AutodeskTemplateSectionKind =
  | "basic_information"
  | "work_log"
  | "materials"
  | "equipment"
  | "signature"
  | "notes"
  | "questions"
  | "custom_table";

export interface AutodeskTemplateSectionSnapshot {
  id: string;
  uid: string;
  label: string;
  description?: string;
  displayIndex: number;
  kind: AutodeskTemplateSectionKind;
  fields: AutodeskTemplateFieldSnapshot[];
  columns: AutodeskTemplateColumnSnapshot[];
}

export interface AutodeskFormLayoutSnapshot {
  version: 1;
  layoutId: string;
  capturedAt: string;
  sections: AutodeskTemplateSectionSnapshot[];
  fieldCount: number;
  tableCount: number;
}

export type AutodeskMappingStrategy =
  | "form_metadata"
  | "table"
  | "question"
  | "notes"
  | "manual";

export type AutodeskQuestionValueKind =
  | "text"
  | "number"
  | "integer"
  | "date"
  | "toggle"
  | "single_choice"
  | "multi_choice"
  | "signature"
  | "unknown";

/**
 * How a canonical fact is projected into an Autodesk question. These modes
 * describe Autodesk's field affordances; none of them is tied to a particular
 * template section or daily-log category.
 */
export type AutodeskQuestionBindingMode =
  | "narrative"
  | "source_value"
  | "item_count"
  | "presence"
  | "preset_match"
  | "form_date"
  | "manual";

export interface AutodeskQuestionBinding {
  sectionId: string;
  sectionLabel: string;
  fieldId: string;
  fieldLabel: string;
  valueKind: AutodeskQuestionValueKind;
  valueName?: string;
  required: boolean;
  presets: string[];
  mode: AutodeskQuestionBindingMode;
  /** Canonical records use their provider-neutral category; genuinely custom
   * template questions accept only records explicitly targeted to fieldId. */
  sourceStrategy?: "canonical_category" | "template_field";
  sourceCategory?: ProcoreDailyLogCategory;
  sourceFieldKeys: string[];
  numericAggregation?: "first" | "sum";
  affirmativePreset?: string;
  negativePreset?: string;
  presencePolarity?: "affirmative_when_present" | "negative_when_present";
  rationale: string;
}

export interface AutodeskDailyLogFieldTarget {
  templateId?: string;
  layoutId: string;
  sectionId: string;
  sectionLabel: string;
  fieldId: string;
  fieldLabel: string;
  valueKind: AutodeskQuestionValueKind;
  presets: string[];
}

export type AutodeskDailyLogDisplayGroupKind =
  | "basic_information"
  | "table"
  | "question"
  | "manual"
  | "notes";

/** A Procore-like row in Led AI's Daily Report UI. Parent Autodesk sections
 * are provenance only; each question is expanded into an independent group. */
export interface AutodeskDailyLogDisplayGroup {
  id: string;
  label: string;
  kind: AutodeskDailyLogDisplayGroupKind;
  sectionId: string;
  sectionLabel: string;
  fieldId?: string;
  schema?: string;
  sourceCategories: ProcoreDailyLogCategory[];
  sourceStrategy?: "canonical_category" | "template_field";
  valueKind?: AutodeskQuestionValueKind;
  required?: boolean;
  presets?: string[];
}

export type AutodeskTableColumnBindingMode =
  | "source_value"
  | "title"
  | "narrative"
  | "item_count";

export interface AutodeskTableColumnBinding {
  columnId: string;
  columnLabel: string;
  columnType?: string;
  mode: AutodeskTableColumnBindingMode;
  sourceFieldKeys: string[];
  /**
   * The column's closed vocabulary, copied from the layout snapshot.
   *
   * Carried on the BINDING (not read from the layout at use time) so the
   * extraction contract can put the allowed values in front of the model
   * without the prompt builder needing the layout. Empty/absent means the
   * column accepts free text — which is a real case, not an oversight: the
   * same built-in Work Log `trade` column has six presets on ACC's
   * "(Sample) Daily Report" and ZERO on a custom "Daily Log" template, so
   * the constraint is per-layout and must never be hardcoded.
   */
  presets?: string[];
}

export interface AutodeskTableBinding {
  sectionId: string;
  sectionLabel: string;
  schema: string;
  sourceCategory: ProcoreDailyLogCategory;
  columns: AutodeskTableColumnBinding[];
  rationale: string;
}

export interface AutodeskDailyLogCategoryMapping {
  category: ProcoreDailyLogCategory;
  categoryLabel: string;
  sectionId: string;
  sectionLabel: string;
  sectionKind: AutodeskTemplateSectionKind;
  strategy: AutodeskMappingStrategy;
  fieldId?: string;
  rationale: string;
}

export interface AutodeskDailyLogMappingPlan {
  version: 1 | 2 | 3;
  layoutId: string;
  generatedAt: string;
  sections: AutodeskTemplateSectionSnapshot[];
  categoryMappings: AutodeskDailyLogCategoryMapping[];
  questionBindings?: AutodeskQuestionBinding[];
  tableBindings?: AutodeskTableBinding[];
  displayGroups?: AutodeskDailyLogDisplayGroup[];
}

export interface AutodeskFormLayoutLike {
  id: string;
  fieldCount: number;
  tableCount: number;
  sections: Array<{
    id: string;
    uid: string;
    label: string;
    description?: string;
    displayIndex: number;
    fields: Array<{
      uid: string;
      label: string;
      type: "question" | "table" | "unknown";
      valueName?: string;
      schema?: string;
      required: boolean;
      presets: string[];
    }>;
    columns: Array<{
      uid: string;
      label: string;
      columnKey?: string;
      columnType?: string;
      presets: string[];
    }>;
  }>;
}

/**
 * ACC form text, folded to the form the layout matcher compares on: camelCase
 * split, lower-cased, and every run of non-alphanumerics collapsed to one
 * space. So `"Drywall/Paint"`, `"Drywall - Paint"` and `"drywall paint"` are
 * one value.
 *
 * Exported because this is ALSO the rule that decides whether a value is one of
 * a column's `presets` — `autodeskDailyLog.service.ts` matched the model's value
 * to a preset with a byte-identical private copy of this function, and the
 * record-level vocabulary filter has to predict that decision exactly. Two
 * copies of an accept rule is how a value gets nulled at extraction that the
 * wire would have published. Deliberately NOT the same as
 * `normalizeVocabularyValue` (case + whitespace only), which governs Procore's
 * company Trade catalog; see `matchAutodeskPreset`.
 */
export const normalizeAutodeskFormText = (value: unknown): string =>
  typeof value === "string"
    ? value
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
    : "";

const normalize = normalizeAutodeskFormText;

/**
 * The preset a value IS, in the preset's own spelling — or undefined.
 *
 * The one place the "is this value allowed in this ACC column?" question is
 * answered: the wire serializer asks it to decide what to send (and sends the
 * preset's spelling, so casing/punctuation drift never 400s), and
 * `canonicalizeAutodeskPresetValues` asks it at extraction so the stored record
 * already holds that spelling.
 *
 * Exact after normalization — never a prefix, a stem or a best fit. "Electric"
 * is not "Electrical"; they are different trades doing different work.
 */
export function matchAutodeskPreset(
  value: unknown,
  presets: readonly string[],
): string | undefined {
  const needle = normalizeAutodeskFormText(value);
  if (!needle) return undefined;
  return presets.find((preset) => normalizeAutodeskFormText(preset) === needle);
}

function sectionHaystack(
  section: Pick<AutodeskFormLayoutLike["sections"][number], "label" | "fields" | "columns">,
): string {
  return normalize([
    section.label,
    ...section.fields.flatMap((field) => [field.label, field.schema]),
    ...section.columns.flatMap((column) => [column.label, column.columnKey]),
  ].join(" "));
}

export function classifyAutodeskTemplateSection(
  section: AutodeskFormLayoutLike["sections"][number],
): AutodeskTemplateSectionKind {
  const haystack = sectionHaystack(section);
  const hasTable = section.fields.some((field) => field.type === "table");
  if (/basic information|form detail|general information/.test(haystack)) return "basic_information";
  if (
    /signature|sign off|signed by/.test(normalize(section.label)) ||
    (section.fields.length > 0 && section.fields.every((field) => field.valueName === "svgVal"))
  ) return "signature";
  if (/\bnotes?\b|additional comments/.test(haystack) && !hasTable) return "notes";
  if (/work log|manpower|labor|labour|work performed|headcount|regular hours/.test(haystack)) return "work_log";
  if (/material|deliver/.test(haystack)) return "materials";
  if (/equipment|machinery/.test(haystack)) return "equipment";
  return hasTable ? "custom_table" : "questions";
}

export function snapshotAutodeskFormLayout(
  layout: AutodeskFormLayoutLike,
  capturedAt = new Date().toISOString(),
): AutodeskFormLayoutSnapshot {
  const sections: AutodeskTemplateSectionSnapshot[] = [...layout.sections]
    .sort((left, right) => left.displayIndex - right.displayIndex)
    .map((section) => ({
      id: section.id,
      uid: section.uid,
      label: section.label,
      ...(section.description ? { description: section.description } : {}),
      displayIndex: section.displayIndex,
      kind: classifyAutodeskTemplateSection(section),
      fields: section.fields.map((field) => ({ ...field, presets: [...field.presets] })),
      columns: section.columns.map((column) => ({ ...column, presets: [...column.presets] })),
    }));
  // Autodesk renders Form Details / Basic Information outside the ordered
  // layout-section payload. Add it as a virtual section so Led AI mirrors what
  // the user actually sees in ACC rather than only what the section endpoint
  // returns.
  if (!sections.some((section) => section.kind === "basic_information")) {
    sections.unshift({
      id: "__basic_information__",
      uid: "__basic_information__",
      label: "Basic Information",
      displayIndex: -1,
      kind: "basic_information",
      fields: [],
      columns: [],
    });
  }
  // Notes is another built-in Form area rendered outside layout sections. It
  // is the deliberate lossless fallback for canonical facts with no safe
  // table/question destination.
  if (!sections.some((section) => section.kind === "notes")) {
    sections.push({
      id: "__notes__",
      uid: "__notes__",
      label: "Notes",
      displayIndex: Number.MAX_SAFE_INTEGER,
      kind: "notes",
      fields: [],
      columns: [],
    });
  }
  return {
    version: 1,
    layoutId: layout.id,
    capturedAt,
    fieldCount: layout.fieldCount,
    tableCount: layout.tableCount,
    sections,
  };
}

const CATEGORY_ALIASES: Record<ProcoreDailyLogCategory, string[]> = {
  weather: ["weather", "conditions", "temperature", "wind", "sky"],
  delays: ["delay", "schedule impact", "lost time"],
  manpower: ["manpower", "labor", "labour", "headcount", "workers"],
  notes: ["notes", "comments", "daily summary"],
  visitors: ["visitor", "guest"],
  phone_calls: ["phone call", "call log"],
  inspections: ["inspection", "test"],
  deliveries: ["delivery", "deliveries", "material"],
  dumpsters: ["dumpster", "haul"],
  work_logs: ["work log", "work performed", "work completed"],
  plan_revisions: ["plan revision", "drawing revision"],
  productivity: ["productivity", "production"],
  safety_violations: ["safety violation", "safety observation", "hazard"],
  accidents: ["accident", "incident", "injury"],
  equipment: ["equipment", "machinery"],
  quantities: ["quantity", "quantities", "installed"],
  waste: ["waste", "debris"],
  photos: ["photo", "image"],
  observations: ["observation", "issue"],
};

const STOP_WORDS = new Set([
  "a", "an", "and", "any", "are", "at", "be", "by", "did", "do", "does",
  "for", "from", "has", "have", "in", "is", "it", "log", "logged", "of",
  "on", "or", "the", "there", "to", "was", "were", "with",
]);

const SEMANTIC_EQUIVALENTS = [
  ["company", "vendor", "contractor", "subcontractor", "sub"],
  ["trade", "discipline", "craft"],
  ["worker", "workers", "headcount", "manpower", "labor", "labour", "crew", "personnel"],
  ["hour", "hours", "timespan", "duration"],
  ["overtime", "ot"],
  ["description", "detail", "details", "comment", "comments", "note", "notes", "summary", "narrative"],
  ["material", "materials", "delivery", "deliveries", "contents", "supply"],
  ["equipment", "machinery", "tool", "tools"],
  ["quantity", "quantities", "count", "amount"],
  ["unit", "uom"],
  ["location", "area", "room", "floor", "zone"],
  ["visitor", "visitors", "guest", "guests"],
  ["phone", "telephone", "call", "calls"],
  ["inspection", "inspections", "test", "tests"],
  ["safety", "hazard", "hazards", "violation", "violations"],
  ["accident", "accidents", "incident", "incidents", "injury", "injuries"],
  ["dumpster", "dumpsters", "container", "containers", "bin", "bins", "haul"],
  ["issue", "issues", "problem", "problems", "damage", "damaged"],
  ["collect", "collected", "collection", "pickup", "picked", "haul", "hauled", "removed", "emptied"],
  ["return", "returned", "replacement", "replaced", "delivered"],
] as const;

const VALUE_CONCEPTS: Array<{ phrases: string[]; keys: string[] }> = [
  { phrases: ["company", "vendor", "contractor"], keys: ["companyId", "companyUid", "vendorId", "vendor", "company"] },
  { phrases: ["trade", "discipline", "craft"], keys: ["trade", "tradeName", "discipline"] },
  { phrases: ["role"], keys: ["roleId", "roleUid", "role"] },
  { phrases: ["workers", "worker", "headcount", "manpower", "crew"], keys: ["workers", "workerCount", "headcount", "count"] },
  { phrases: ["overtime", "ot hours", "ot"], keys: ["overtimeHours", "otHours"] },
  { phrases: ["hours used", "operating hours"], keys: ["hoursUsed", "hoursOperating", "durationHours", "hours"] },
  { phrases: ["regular hours", "hours", "timespan", "duration"], keys: ["hours", "regularHours", "workHours", "hoursUsed", "durationHours"] },
  { phrases: ["material", "contents", "delivery"], keys: ["contents", "material", "materialName", "item"] },
  { phrases: ["equipment", "machinery", "tool"], keys: ["equipmentName", "equipment", "item"] },
  { phrases: ["quantity", "count", "amount"], keys: ["quantity", "count"] },
  { phrases: ["unit", "uom"], keys: ["unit", "uom"] },
  { phrases: ["location", "area", "room", "floor", "zone"], keys: ["location", "area", "room"] },
  { phrases: ["date"], keys: ["date", "observedDate"] },
  { phrases: ["time"], keys: ["time", "startTime", "endTime"] },
];

function stemToken(token: string): string {
  if (token.length > 5 && token.endsWith("ing")) return token.slice(0, -3);
  if (token.length > 4 && token.endsWith("ied")) return `${token.slice(0, -3)}y`;
  if (token.length > 4 && token.endsWith("ed")) return token.slice(0, -2);
  if (token.length > 4 && token.endsWith("ies")) return `${token.slice(0, -3)}y`;
  if (token.length > 3 && token.endsWith("s")) return token.slice(0, -1);
  return token;
}

export function autodeskSemanticTerms(value: unknown): string[] {
  const terms = new Set(
    normalize(value)
      .split(" ")
      .filter((term) => !STOP_WORDS.has(term))
      .map(stemToken)
      .filter((term) => term.length > 1 && !STOP_WORDS.has(term)),
  );
  for (const group of SEMANTIC_EQUIVALENTS) {
    if (group.some((term) => terms.has(stemToken(term)))) terms.add(stemToken(group[0]));
  }
  return [...terms];
}

function overlapScore(left: string[], right: string[]): number {
  const rightSet = new Set(right);
  return left.reduce((score, term) => score + (rightSet.has(term) ? 1 : 0), 0);
}

function containsPhrase(target: string, phrase: string): boolean {
  const normalizedTarget = normalize(target);
  const normalizedPhrase = normalize(phrase);
  return Boolean(
    normalizedPhrase && ` ${normalizedTarget} `.includes(` ${normalizedPhrase} `),
  );
}

function categoryPhrases(category: ProcoreDailyLogCategory): string[] {
  const definition = PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[category];
  return [
    definition.label,
    definition.shortLabel ?? "",
    ...CATEGORY_ALIASES[category],
  ].filter(Boolean);
}

function categoryTerms(category: ProcoreDailyLogCategory): string[] {
  // Destination discovery must use category identity, not generic canonical
  // field words such as "comments", "time", or "location". Those common
  // words caused unrelated sections to look compatible. Field-level matching
  // is handled separately after a category destination is established.
  return autodeskSemanticTerms(categoryPhrases(category).join(" "));
}

function categoryMatchScore(target: string, category: ProcoreDailyLogCategory): number {
  const normalizedTarget = normalize(target);
  if (!normalizedTarget) return 0;
  const phraseScore = categoryPhrases(category).reduce((score, phrase) => {
    const normalizedPhrase = normalize(phrase);
    return score + (normalizedPhrase && containsPhrase(normalizedTarget, normalizedPhrase) ? 12 : 0);
  }, 0);
  return phraseScore + overlapScore(autodeskSemanticTerms(target), categoryTerms(category)) * 4;
}

function sourceKeysForTarget(
  category: ProcoreDailyLogCategory,
  target: string,
  compatibleTypes?: Set<string>,
): string[] {
  const definition = PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[category];
  const targetTerms = autodeskSemanticTerms(target);
  const fields = definition.fields
    .filter((field) => !compatibleTypes || compatibleTypes.has(field.type))
    .map((field) => {
      const phrases = [field.key, field.label];
      const exact = phrases.some((phrase) => containsPhrase(target, phrase)) ? 10 : 0;
      return {
        field,
        score: exact + overlapScore(targetTerms, autodeskSemanticTerms(phrases.join(" "))) * 3,
      };
    })
    .sort((left, right) => right.score - left.score);
  const best = fields[0];
  const rankedConcepts = VALUE_CONCEPTS
    .map((concept) => ({
      concept,
      score:
        concept.phrases.reduce(
          (score, phrase) => {
            if (!containsPhrase(target, phrase)) return score;
            const tokenCount = normalize(phrase).split(" ").length;
            return score + tokenCount * tokenCount * 8;
          },
          0,
        ) + overlapScore(
          targetTerms,
          // The first phrase is the concept's semantic identity; remaining
          // phrases are exact aliases. Including every alias in fuzzy overlap
          // made "OT hours" leak into an unrelated "Regular hours" column.
          autodeskSemanticTerms(concept.phrases[0]),
        ),
    }))
    .filter((candidate) => candidate.score > 0)
    .sort((left, right) => right.score - left.score);
  // Prefer the most-specific value concept instead of concatenating every
  // partial match. For example, "# OT hours" matches both "overtime" and the
  // generic "hours" concept; including both would incorrectly copy regular
  // hours into OT whenever overtime is absent.
  const topConceptScore = rankedConcepts[0]?.score ?? 0;
  const keys = rankedConcepts
    .filter((candidate) => candidate.score === topConceptScore)
    .flatMap(({ concept }) => concept.keys);
  // A canonical field can still be more precise than a generic concept (for
  // example "Compliance due date" vs the broad date concept). Add it only
  // when its own label/key match is at least as strong as the selected concept.
  if (best && best.score >= 3 && best.score >= topConceptScore)
    keys.push(best.field.key);
  return [...new Set(keys)];
}

function hasMatchingCanonicalField(
  category: ProcoreDailyLogCategory,
  target: string,
  types: Set<string>,
): boolean {
  const targetTerms = autodeskSemanticTerms(target);
  return PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[category].fields
    .filter((field) => types.has(field.type))
    .some((field) =>
      containsPhrase(target, field.key) ||
      containsPhrase(target, field.label) ||
      overlapScore(
        targetTerms,
        autodeskSemanticTerms(`${field.key} ${field.label}`),
      ) > 0,
    );
}

export function autodeskQuestionValueKind(
  field: AutodeskTemplateFieldSnapshot,
): AutodeskQuestionValueKind {
  if (field.valueName === "textVal") return "text";
  if (field.valueName === "numberVal") return "number";
  if (field.valueName === "integerVal") return "integer";
  if (field.valueName === "dateVal") return "date";
  if (field.valueName === "toggleVal") return "toggle";
  if (field.valueName === "choiceVal") return "single_choice";
  if (field.valueName === "arrayVal") return "multi_choice";
  if (field.valueName === "svgVal") return "signature";
  return "unknown";
}

export function autodeskNegativePreset(presets: string[]): string | undefined {
  return presets.find((preset) => {
    const candidate = normalize(preset);
    return /^(no|none|not applicable|n a)\b/.test(candidate) || /\b(without|no activity)\b/.test(candidate);
  });
}

function affirmativePreset(presets: string[]): string | undefined {
  return presets.find((preset) => /^(yes|true|affirmative)$/i.test(preset.trim()));
}

function negativePresencePreset(presets: string[]): string | undefined {
  return presets.find((preset) => /^(no|false|negative)$/i.test(preset.trim()));
}

function bestCategoryForQuestion(
  section: AutodeskTemplateSectionSnapshot,
  field: AutodeskTemplateFieldSnapshot,
  excluded: Set<ProcoreDailyLogCategory>,
): ProcoreDailyLogCategory | undefined {
  const ranked = PROCORE_DAILY_LOG_CATEGORY_ORDER
    .filter((category) => category !== "observations" && !excluded.has(category))
    .map((category) => ({
      category,
      // The control label is the primary contract. Section labels are useful
      // context, but a mixed "Conditions & Occurrences" section must not turn
      // every child control into weather merely because it contains that word.
      score: categoryMatchScore(field.label, category) * 5 + categoryMatchScore(section.label, category),
    }))
    .sort((left, right) => right.score - left.score);
  const best = ranked[0];
  const runnerUp = ranked[1];
  if (!best || best.score < 12) return undefined;
  if (runnerUp && best.score - runnerUp.score < 2) return undefined;
  return best.category;
}

function questionBindingFor(
  section: AutodeskTemplateSectionSnapshot,
  field: AutodeskTemplateFieldSnapshot,
  tableCategories: Set<ProcoreDailyLogCategory>,
): AutodeskQuestionBinding | null {
  const valueKind = autodeskQuestionValueKind(field);
  const base = {
    sectionId: section.id,
    sectionLabel: section.label,
    // Autodesk's layout/detail reader addresses the question definition by
    // uid, but Form values:batch-update addresses the value slot by schema.
    // Older/fixture layouts omit schema, where uid remains the compatible id.
    fieldId: field.schema ?? field.uid,
    fieldLabel: field.label,
    valueKind,
    ...(field.valueName ? { valueName: field.valueName } : {}),
    required: field.required,
    presets: [...field.presets],
  };
  if (valueKind === "signature" || valueKind === "unknown") {
    return {
      ...base,
      mode: "manual",
      sourceStrategy: "template_field",
      sourceFieldKeys: [],
      rationale: `${field.label} uses an Autodesk ${valueKind} affordance and remains manual.`,
    };
  }
  const target = `${section.label} ${field.label}`;
  const normalizedFieldLabel = normalize(field.label);
  if (
    valueKind === "date" &&
    /^(date|(form|report|daily report) date|date of (form|report))$/.test(
      normalizedFieldLabel,
    )
  ) {
    return {
      ...base,
      mode: "form_date",
      sourceFieldKeys: [],
      rationale: `${field.label} receives the form date.`,
    };
  }
  const sourceCategory = bestCategoryForQuestion(section, field, tableCategories);
  if (!sourceCategory) {
    const sourceFieldKeys = ["comment", "comments", "value", "response", "description", "details"];
    if (valueKind === "text" || valueKind === "number" || valueKind === "integer" || valueKind === "date") {
      return {
        ...base,
        mode: "source_value",
        sourceStrategy: "template_field",
        sourceFieldKeys,
        ...(valueKind === "number" || valueKind === "integer"
          ? { numericAggregation: "first" as const }
          : {}),
        rationale: `${field.label} accepts evidence explicitly targeted to this template field.`,
      };
    }
    const yes = affirmativePreset(field.presets);
    const no = negativePresencePreset(field.presets);
    if (valueKind === "toggle" || (valueKind === "single_choice" && yes && no)) {
      const negativeWhenPresent = /\b(no|none|without|free of|compliant|safe|satisfactory)\b/.test(
        normalize(field.label),
      );
      return {
        ...base,
        mode: "presence",
        sourceStrategy: "template_field",
        sourceFieldKeys,
        ...(yes ? { affirmativePreset: yes } : {}),
        ...(no ? { negativePreset: no } : {}),
        presencePolarity: negativeWhenPresent ? "negative_when_present" : "affirmative_when_present",
        rationale: `${field.label} is answered from evidence explicitly targeted to this template field.`,
      };
    }
    if ((valueKind === "single_choice" || valueKind === "multi_choice") && field.presets.length > 0) {
      const negative = autodeskNegativePreset(field.presets);
      return {
        ...base,
        mode: "preset_match",
        sourceStrategy: "template_field",
        sourceFieldKeys,
        ...(negative ? { negativePreset: negative } : {}),
        rationale: `${field.label} selects declared presets from evidence explicitly targeted to this template field.`,
      };
    }
    return {
      ...base,
      mode: "manual",
      sourceStrategy: "template_field",
      sourceFieldKeys: [],
      rationale: `${field.label} has no safe writable template affordance and remains manual.`,
    };
  }
  const compatible =
    valueKind === "number" || valueKind === "integer"
      // Numeric Autodesk controls can safely parse a canonically text-typed
      // reading such as `temperature: "72"`; failed parses remain unhandled.
      ? new Set(["number", "text"])
      : valueKind === "date"
        ? new Set(["date"])
        : valueKind === "toggle"
          ? new Set(["boolean"])
          : undefined;
  const sourceFieldKeys = sourceKeysForTarget(sourceCategory, target, compatible);
  if (valueKind === "text") {
    return {
      ...base,
      mode: sourceFieldKeys.length > 0 ? "source_value" : "narrative",
      sourceStrategy: "canonical_category",
      sourceCategory,
      sourceFieldKeys,
      rationale: `${field.label} receives ${sourceFieldKeys.length > 0 ? "the matching canonical value" : "the canonical narrative"}.`,
    };
  }
  if (valueKind === "number" || valueKind === "integer") {
    const countQuestion = /\b(count|number|how many|total)\b/.test(normalize(field.label));
    const totalQuestion = /\b(total|sum|combined|overall|all|count|number|how many)\b/.test(
      normalize(field.label),
    );
    const hasCanonicalNumberField = hasMatchingCanonicalField(
      sourceCategory,
      target,
      new Set(["number"]),
    );
    const hasCanonicalTextReading = !countQuestion && hasMatchingCanonicalField(
      sourceCategory,
      target,
      new Set(["text"]),
    );
    const hasCanonicalNumericField =
      hasCanonicalNumberField || hasCanonicalTextReading;
    if (!hasCanonicalNumericField && !countQuestion) return null;
    return {
      ...base,
      mode: hasCanonicalNumericField ? "source_value" : "item_count",
      sourceStrategy: "canonical_category",
      sourceCategory,
      sourceFieldKeys: hasCanonicalNumericField ? sourceFieldKeys : [],
      ...(hasCanonicalNumericField
        ? { numericAggregation: totalQuestion ? "sum" as const : "first" as const }
        : {}),
      rationale: `${field.label} receives ${hasCanonicalNumericField ? "the matching numeric value" : "the number of matching records"}.`,
    };
  }
  if (valueKind === "date") {
    if (sourceFieldKeys.length === 0) return null;
    return {
      ...base,
      mode: "source_value",
      sourceStrategy: "canonical_category",
      sourceCategory,
      sourceFieldKeys,
      rationale: `${field.label} receives the matching canonical date.`,
    };
  }
  const yes = affirmativePreset(field.presets);
  const no = negativePresencePreset(field.presets);
  if (valueKind === "toggle" || (valueKind === "single_choice" && yes && no)) {
    const hasCanonicalBooleanField = valueKind === "toggle" && hasMatchingCanonicalField(
      sourceCategory,
      target,
      new Set(["boolean"]),
    );
    const negativeWhenPresent = /\b(no|none|without|free of|compliant|safe|satisfactory)\b/.test(
      normalize(field.label),
    );
    return {
      ...base,
      mode: hasCanonicalBooleanField ? "source_value" : "presence",
      sourceStrategy: "canonical_category",
      sourceCategory,
      sourceFieldKeys: hasCanonicalBooleanField ? sourceFieldKeys : [],
      ...(yes ? { affirmativePreset: yes } : {}),
      ...(no ? { negativePreset: no } : {}),
      presencePolarity: negativeWhenPresent ? "negative_when_present" : "affirmative_when_present",
      rationale: `${field.label} is answered from the presence or absence of matching canonical facts.`,
    };
  }
  if (valueKind === "single_choice" || valueKind === "multi_choice") {
    if (field.presets.length === 0) return null;
    const negative = autodeskNegativePreset(field.presets);
    return {
      ...base,
      mode: "preset_match",
      sourceStrategy: "canonical_category",
      sourceCategory,
      sourceFieldKeys,
      ...(negative ? { negativePreset: negative } : {}),
      rationale: `${field.label} selects only template presets supported by canonical evidence.`,
    };
  }
  return null;
}

function canonicalDisplayLabel(category: ProcoreDailyLogCategory): string {
  const definition = PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[category];
  return definition.shortLabel ?? definition.label;
}

export function buildAutodeskDailyLogDisplayGroups(
  sections: AutodeskTemplateSectionSnapshot[],
  tableBindings: AutodeskTableBinding[],
  questionBindings: AutodeskQuestionBinding[],
): AutodeskDailyLogDisplayGroup[] {
  const groups: AutodeskDailyLogDisplayGroup[] = [];
  for (const section of sections) {
    if (section.kind === "basic_information") {
      groups.push({
        id: section.id,
        label: section.label,
        kind: "basic_information",
        sectionId: section.id,
        sectionLabel: section.label,
        sourceCategories: [],
      });
      continue;
    }
    if (section.kind === "notes") {
      groups.push({
        id: section.id,
        label: section.label,
        kind: "notes",
        sectionId: section.id,
        sectionLabel: section.label,
        sourceCategories: [],
      });
      continue;
    }

    const schemas = [...new Set(
      section.fields
        .filter((field) => field.type === "table" && field.schema)
        .map((field) => field.schema as string),
    )];
    for (const schema of schemas) {
      const bindings = tableBindings.filter(
        (binding) => binding.sectionId === section.id && binding.schema === schema,
      );
      groups.push({
        id: `table:${section.id}:${schema}`,
        label: section.label,
        kind: "table",
        sectionId: section.id,
        sectionLabel: section.label,
        schema,
        sourceCategories: [...new Set(bindings.map((binding) => binding.sourceCategory))],
        sourceStrategy: "canonical_category",
      });
    }

    for (const field of section.fields.filter((candidate) => candidate.type === "question")) {
      const binding = questionBindings.find(
        (candidate) => candidate.fieldId === (field.schema ?? field.uid),
      );
      if (!binding) continue;
      const manual = binding.mode === "manual";
      groups.push({
        id: `question:${field.uid}`,
        label: binding.sourceCategory
          ? canonicalDisplayLabel(binding.sourceCategory)
          : field.label,
        kind: manual ? "manual" : "question",
        sectionId: section.id,
        sectionLabel: section.label,
        fieldId: binding.fieldId,
        sourceCategories: binding.sourceCategory ? [binding.sourceCategory] : [],
        sourceStrategy: binding.sourceStrategy ?? (
          binding.sourceCategory ? "canonical_category" : "template_field"
        ),
        valueKind: binding.valueKind,
        required: binding.required,
        presets: [...binding.presets],
      });
    }
  }
  return groups;
}

function columnBindingFor(
  category: ProcoreDailyLogCategory,
  column: AutodeskTemplateColumnSnapshot,
): AutodeskTableColumnBinding | null {
  const binding = baseColumnBindingFor(category, column);
  if (!binding) return null;
  // Attached here rather than in each of the six return sites below, so a new
  // branch cannot silently ship a preset-bearing column with no vocabulary.
  return column.presets?.length
    ? { ...binding, presets: [...column.presets] }
    : binding;
}

function baseColumnBindingFor(
  category: ProcoreDailyLogCategory,
  column: AutodeskTemplateColumnSnapshot,
): AutodeskTableColumnBinding | null {
  const target = `${column.columnKey ?? ""} ${column.label}`;
  const normalizedTarget = normalize(target);
  const normalizedColumnType = normalize(column.columnType).replace(/ /g, "_");
  if (normalizedColumnType === "uid_val") {
    const sourceFieldKeys = /\b(company|vendor|contractor|subcontractor)\b/.test(normalizedTarget)
      ? ["companyId"]
      : /\b(role|position)\b/.test(normalizedTarget)
        ? ["roleId"]
        : [];
    return sourceFieldKeys.length > 0
      ? {
          columnId: column.uid,
          columnLabel: column.label,
          ...(column.columnType ? { columnType: column.columnType } : {}),
          mode: "source_value",
          sourceFieldKeys,
        }
      : null;
  }
  if (/\b(description|detail|comment|note|summary|work performed)\b/.test(normalizedTarget)) {
    // ACC's built-in Work Log calls the narrow Procore-style manpower
    // comments column "Work performed". It should receive the same concise
    // 2-5 word tag as Procore, not the item's title/summary/location narrative.
    if (category === "manpower") {
      return {
        columnId: column.uid,
        columnLabel: column.label,
        ...(column.columnType ? { columnType: column.columnType } : {}),
        mode: "source_value",
        sourceFieldKeys: ["comments"],
      };
    }
    return {
      columnId: column.uid,
      columnLabel: column.label,
      ...(column.columnType ? { columnType: column.columnType } : {}),
      mode: "narrative",
      sourceFieldKeys: [],
    };
  }
  const sourceFieldKeys = sourceKeysForTarget(category, target);
  if (sourceFieldKeys.length > 0) {
    return {
      columnId: column.uid,
      columnLabel: column.label,
      ...(column.columnType ? { columnType: column.columnType } : {}),
      mode: "source_value",
      sourceFieldKeys,
    };
  }
  if (/\b(title|name|item|subject)\b/.test(normalizedTarget)) {
    return {
      columnId: column.uid,
      columnLabel: column.label,
      ...(column.columnType ? { columnType: column.columnType } : {}),
      mode: "title",
      sourceFieldKeys: [],
    };
  }
  if (/\b(count|number|total)\b/.test(normalizedTarget)) {
    return {
      columnId: column.uid,
      columnLabel: column.label,
      ...(column.columnType ? { columnType: column.columnType } : {}),
      mode: "item_count",
      sourceFieldKeys: [],
    };
  }
  return null;
}

function buildTableBindings(
  sections: AutodeskTemplateSectionSnapshot[],
): AutodeskTableBinding[] {
  const tables = sections.filter((section) => section.fields.some((field) => field.type === "table" && field.schema));
  const bindings: AutodeskTableBinding[] = [];
  for (const category of PROCORE_DAILY_LOG_CATEGORY_ORDER) {
    if (category === "observations" || category === "photos") continue;
    const ranked = tables
      .map((section) => ({
        section,
        score:
          categoryMatchScore(section.label, category) * 3 +
          categoryMatchScore(
            section.columns.map((column) => `${column.columnKey ?? ""} ${column.label}`).join(" "),
            category,
          ),
      }))
      .sort((left, right) => right.score - left.score);
    const best = ranked[0];
    const runnerUp = ranked[1];
    if (!best || best.score < 12) continue;
    if (runnerUp && best.score - runnerUp.score < 3) continue;
    const schema = best.section.fields.find((field) => field.type === "table" && field.schema)?.schema;
    if (!schema) continue;
    const columns = best.section.columns
      .map((column) => columnBindingFor(category, column))
      .filter((binding): binding is AutodeskTableColumnBinding => binding !== null);
    if (columns.length === 0) continue;
    bindings.push({
      sectionId: best.section.id,
      sectionLabel: best.section.label,
      schema,
      sourceCategory: category,
      columns,
      rationale: `${PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[category].label} matches the table label and supported columns.`,
    });
  }
  return bindings;
}

function mappingFor(
  category: ProcoreDailyLogCategory,
  sections: AutodeskTemplateSectionSnapshot[],
  tableBindings: AutodeskTableBinding[],
  questionBindings: AutodeskQuestionBinding[],
): AutodeskDailyLogCategoryMapping {
  const definition = PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[category];
  const label = definition.shortLabel ?? definition.label;
  const table = tableBindings.find((binding) => binding.sourceCategory === category);
  const question = questionBindings.find((binding) => binding.sourceCategory === category && binding.mode !== "manual");
  const target = table ?? question;
  if (target) {
    const section = sections.find((candidate) => candidate.id === target.sectionId);
    return {
      category,
      categoryLabel: label,
      sectionId: target.sectionId,
      sectionLabel: target.sectionLabel,
      sectionKind: section?.kind ?? "questions",
      strategy: table ? "table" : "question",
      ...(!table && question ? { fieldId: question.fieldId } : {}),
      rationale: target.rationale,
    };
  }
  const notes = sections.find((section) => section.kind === "notes");
  return {
    category,
    categoryLabel: label,
    sectionId: notes?.id ?? "__notes__",
    sectionLabel: notes?.label ?? "Notes",
    sectionKind: "notes",
    strategy: "notes",
    rationale: `No safe ${label.toLowerCase()} affordance exists, so this is preserved in Notes.`,
  };
}

export function buildAutodeskDailyLogMappingPlan(
  layout: AutodeskFormLayoutLike | AutodeskFormLayoutSnapshot,
  generatedAt = new Date().toISOString(),
): AutodeskDailyLogMappingPlan {
  const snapshot = "layoutId" in layout
    ? layout
    : snapshotAutodeskFormLayout(layout, generatedAt);
  const tableBindings = buildTableBindings(snapshot.sections);
  const tableCategories = new Set(tableBindings.map((binding) => binding.sourceCategory));
  const questionBindings = snapshot.sections.flatMap((section) =>
    section.fields
      .filter((field) => field.type === "question")
      .map((field) => questionBindingFor(section, field, tableCategories))
      .filter((binding): binding is AutodeskQuestionBinding => binding !== null),
  );
  return {
    version: 3,
    layoutId: snapshot.layoutId,
    generatedAt,
    sections: snapshot.sections,
    categoryMappings: PROCORE_DAILY_LOG_CATEGORY_ORDER
      .filter((category) => category !== "observations")
      .map((category) => mappingFor(category, snapshot.sections, tableBindings, questionBindings)),
    questionBindings,
    tableBindings,
    displayGroups: buildAutodeskDailyLogDisplayGroups(
      snapshot.sections,
      tableBindings,
      questionBindings,
    ),
  };
}

export function autodeskDisplayGroupForItem(
  plan: AutodeskDailyLogMappingPlan | null | undefined,
  item: {
    category: string;
    autodeskFieldTarget?: Pick<AutodeskDailyLogFieldTarget, "fieldId"> | null;
  },
): AutodeskDailyLogDisplayGroup | undefined {
  const groups = plan?.displayGroups ?? [];
  const fieldId = item.autodeskFieldTarget?.fieldId;
  if (fieldId) {
    const direct = groups.find((group) => group.fieldId === fieldId);
    if (direct) return direct;
  }
  return groups.find((group) =>
    group.sourceCategories.includes(item.category as ProcoreDailyLogCategory),
  ) ?? groups.find((group) => group.kind === "notes");
}

/** Canonical categories that have a first-class destination in the selected
 * Autodesk template. Template-only questions use `notes` as their neutral
 * extraction envelope and are distinguished by their immutable field target. */
export function getAutodeskTemplateExtractionCategories(
  plan: AutodeskDailyLogMappingPlan | null | undefined,
): ProcoreDailyLogCategory[] {
  if (!plan) return [];
  const categories = new Set<ProcoreDailyLogCategory>();
  for (const binding of plan.tableBindings ?? []) {
    categories.add(binding.sourceCategory);
  }
  for (const binding of plan.questionBindings ?? []) {
    if (binding.mode === "manual") continue;
    if (binding.sourceCategory) categories.add(binding.sourceCategory);
    if (binding.sourceStrategy === "template_field") categories.add("notes");
  }
  if ((plan.displayGroups ?? []).some((group) => group.kind === "notes")) {
    categories.add("notes");
  }
  return PROCORE_DAILY_LOG_CATEGORY_ORDER.filter((category) => categories.has(category));
}

/** Server-side trust boundary for AI-returned Autodesk field ids. */
export function resolveAutodeskDailyLogFieldTarget(
  plan: AutodeskDailyLogMappingPlan | null | undefined,
  fieldId: string | null | undefined,
  templateId?: string,
): AutodeskDailyLogFieldTarget | undefined {
  if (!plan || !fieldId) return undefined;
  const binding = (plan.questionBindings ?? []).find(
    (candidate) =>
      candidate.fieldId === fieldId &&
      candidate.sourceStrategy === "template_field" &&
      candidate.mode !== "manual",
  );
  if (!binding) return undefined;
  return {
    ...(templateId ? { templateId } : {}),
    layoutId: plan.layoutId,
    sectionId: binding.sectionId,
    sectionLabel: binding.sectionLabel,
    fieldId: binding.fieldId,
    fieldLabel: binding.fieldLabel,
    valueKind: binding.valueKind,
    presets: [...binding.presets],
  };
}

/** Compact contract appended to the extraction prompt. Parent Autodesk
 * sections are intentionally provenance only: every child question is listed
 * as an independent Led AI category. */
/**
 * Backfill each table column's closed vocabulary onto an ALREADY PERSISTED plan.
 *
 * `jobsites.autodesk.dailyLogForm.mappingPlan` is stored once at template-select
 * time and reused verbatim. Every plan written before column `presets` existed
 * therefore carries none, so the extraction contract would emit nothing for
 * EVERY existing site — including the customer this was built for — while all
 * the unit tests passed against freshly built plans. Read the vocabulary back
 * out of the snapshot that is stored alongside it.
 *
 * Deliberately additive: only `presets` is touched, so a persisted plan's
 * bindings, modes and rationales are preserved exactly. Returns the plan
 * unchanged when there is no snapshot to read.
 */
export function withColumnPresetsFromLayout(
  plan: AutodeskDailyLogMappingPlan | null | undefined,
  layout: { sections?: Array<{ id?: string; uid?: string; columns?: AutodeskTemplateColumnSnapshot[] }> } | null | undefined,
): AutodeskDailyLogMappingPlan | null | undefined {
  if (!plan || !layout?.sections?.length || !plan.tableBindings?.length) return plan;
  const presetsByColumnUid = new Map<string, string[]>();
  for (const section of layout.sections) {
    for (const column of section.columns ?? []) {
      if (column?.uid && column.presets?.length) presetsByColumnUid.set(column.uid, column.presets);
    }
  }
  if (presetsByColumnUid.size === 0) return plan;
  return {
    ...plan,
    tableBindings: plan.tableBindings.map((binding) => ({
      ...binding,
      columns: binding.columns.map((column) => {
        const presets = presetsByColumnUid.get(column.columnId);
        return presets && !column.presets?.length ? { ...column, presets: [...presets] } : column;
      }),
    })),
  };
}

/**
 * The ACC half of the shared vocabulary: every preset-bearing table column
 * whose category actually declares the field it binds to.
 *
 * Exported because BOTH the prompt (buildAutodeskTemplateExtractionContract)
 * and the record-level filter need the same list, and deriving it twice is how
 * the two drift apart.
 */
export function buildAutodeskVocabularyTargets(
  plan: AutodeskDailyLogMappingPlan | null | undefined,
): TradeVocabularyTarget[] {
  return (plan?.tableBindings ?? []).flatMap((binding) =>
    binding.columns
      .map((column) => ({
        column,
        // The `.find` over PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS is load-bearing
        // — DO NOT drop it as a redundant lookup.
        //
        // A column's `sourceFieldKeys` are the keys the mapper will read, but a
        // table binding receives records from more than one category: the Work
        // Log table also takes `work_logs` and Materials also takes `deliveries`,
        // and neither of those categories DECLARES the bound field. Meanwhile
        // `normalizeProcoreDailyLogFieldValues` strips any key its category does
        // not declare — so the model cannot deliver a value on that key even if
        // it obeyed. Constraining a field the record type has no home for is
        // noise in the prompt at best, and a phantom rejection at worst.
        //
        // So: emit a target only for the field key the binding's OWN category
        // actually declares.
        fieldKey: column.sourceFieldKeys.find((key) =>
          PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[binding.sourceCategory]?.fields.some(
            (field) => field.key === key,
          ),
        ),
      }))
      .filter(({ column, fieldKey }) => Boolean(column.presets?.length && fieldKey))
      .map(({ column, fieldKey }) => ({
        recordType: binding.sourceCategory,
        fieldKey: fieldKey as string,
        fieldLabel: column.columnLabel,
        allowed: column.presets as string[],
      })),
  );
}

/**
 * Rewrite every ACC-constrained field to its preset's OWN spelling, before the
 * record-level vocabulary filter runs.
 *
 * Why this exists as a separate pass: the filter judges with
 * `normalizeVocabularyValue` (case + whitespace only, the rule Procore's
 * company Trade catalog needs), while ACC's wire accepts anything that matches
 * a preset under `normalizeAutodeskFormText` (punctuation folded too). So a
 * Discipline ACC WOULD have taken — "Drywall Paint" against the preset
 * "Drywall/Paint" — got nulled at extraction and never published, where before
 * the filter existed it published fine.
 *
 * Canonicalizing first makes the filter no stricter than the wire without
 * loosening the filter for Procore, and has a second benefit: what the reviewer
 * sees and approves is now literally the string ACC will receive.
 *
 * Values that match no preset are left ALONE — the filter is what nulls them,
 * and it reports them. An empty `allowed` is no constraint, so it is skipped.
 */
export function canonicalizeAutodeskPresetValues<
  T extends { category?: string; fieldValues?: Record<string, unknown> },
>(items: T[], targets: TradeVocabularyTarget[]): T[] {
  const constrained = targets.filter((target) => target.allowed.length > 0);
  if (constrained.length === 0) return items;

  return items.map((item) => {
    const fieldValues = item.fieldValues;
    if (!fieldValues) return item;

    let patch: Record<string, unknown> | null = null;
    for (const target of constrained) {
      if (target.recordType !== item.category) continue;
      const raw = fieldValues[target.fieldKey];
      if (typeof raw !== "string" || !raw.trim()) continue;
      const preset = matchAutodeskPreset(raw, target.allowed);
      if (preset === undefined || preset === raw) continue;
      patch ??= {};
      patch[target.fieldKey] = preset;
    }

    return patch ? { ...item, fieldValues: { ...fieldValues, ...patch } } : item;
  });
}

export function buildAutodeskTemplateExtractionContract(
  plan: AutodeskDailyLogMappingPlan | null | undefined,
): string {
  if (!plan) return "";
  const lines = (plan.displayGroups ?? []).flatMap((group) => {
    if (group.kind === "basic_information" || group.kind === "manual") return [];
    if (group.kind === "notes") {
      return [`- ${group.label}: use category notes for meaningful report facts that do not fit another listed category.`];
    }
    if (group.sourceStrategy === "template_field" && group.fieldId) {
      const choices = group.presets?.length
        ? ` Allowed choices: ${group.presets.join(" | ")}.`
        : "";
      return [
        `- ${group.label}: emit category notes with autodesk_field_id=${group.fieldId}; put the supported response and details in fields.comment.${choices}`,
      ];
    }
    if (group.sourceCategories.length > 0) {
      return [
        `- ${group.label}: emit ${group.sourceCategories.map((category) => `category ${category}`).join(" or ")}.`,
      ];
    }
    return [];
  });
  // ACC's Discipline is a TABLE column. Question fields have had their allowed
  // choices since this contract was written; table columns had not, which is how
  // a free-text Discipline reached ACC and failed the whole form (LED-603).
  // The wording now lives in tradeVocabulary.ts so Procore says the same thing.
  const constrainedColumns = renderTradeVocabularyContract(buildAutodeskVocabularyTargets(plan));
  const allLines = [...lines, ...constrainedColumns];
  return allLines.length > 0
    ? `\nAutodesk daily-report template contract:\n${allLines.join("\n")}\n- Treat every listed question as an independent daily-report category; never create a parent section record.\n- For yes/no or toggle questions, emit a record only when the visit contains affirmative evidence about that topic. Absence is not evidence: the publisher deterministically answers No from the complete reviewed day.\n- For multi-choice questions, preserve the exact supported choice wording in fields.comment when evidence supports it.\n- Never invent autodesk_field_id values or attach one to a canonical category.`
    : "";
}

export function autodeskMappingForCategory(
  plan: AutodeskDailyLogMappingPlan | null | undefined,
  category: string,
): AutodeskDailyLogCategoryMapping | undefined {
  return plan?.categoryMappings.find((mapping) => mapping.category === category);
}

export function autodeskMappingCoverage(plan: AutodeskDailyLogMappingPlan): {
  structured: number;
  fallback: number;
  manualSections: number;
} {
  const manualSectionIds = new Set([
    ...plan.sections
      .filter((section) => section.kind === "signature")
      .map((section) => section.id),
    ...(plan.questionBindings ?? [])
      .filter((binding) => binding.mode === "manual")
      .map((binding) => binding.sectionId),
  ]);
  return {
    structured: plan.categoryMappings.filter((mapping) => mapping.strategy === "table" || mapping.strategy === "question").length,
    fallback: plan.categoryMappings.filter((mapping) => mapping.strategy === "notes").length,
    manualSections: manualSectionIds.size,
  };
}

export interface AutodeskDailyLogSummary {
  /** Rows in the daily-log preview list — `plan.displayGroups.length`. */
  categories: number;
  /** Total Autodesk layout fields (questions + table columns), from the layout — never recomputed. */
  fields: number;
  /** Of `fields`, how many Led AI can actually write (non-manual question or bound table column/widget). */
  fillableFields: number;
  /** Total Autodesk layout tables, from the layout — never recomputed. */
  tables: number;
}

/**
 * Sibling of `autodeskMappingCoverage` for the daily-log form-picker/preview
 * sheets (Task 7): the field- and table-level counts behind "Led AI can fill
 * 18 of 19 fields" and "6 categories · 19 fields · 3 tables". Deliberately a
 * separate function — `autodeskMappingCoverage`'s `{ structured, fallback,
 * manualSections }` shape is depended on by `formatAutodeskCoverageLine` and
 * the live `AutodeskDailyLogPreview` and must not change.
 *
 * `fields`/`tables` come straight from `layout` (Autodesk's own reported
 * counts); `categories`/`fillableFields` are derived by walking the plan.
 * Every number is a function of its input — a different plan/layout always
 * yields different numbers.
 */
export function autodeskDailyLogSummary(
  plan: AutodeskDailyLogMappingPlan,
  layout: Pick<AutodeskFormLayoutLike, "fieldCount" | "tableCount">,
): AutodeskDailyLogSummary {
  const questionBindings = plan.questionBindings ?? [];
  const tableBindings = plan.tableBindings ?? [];

  let fillableFields = 0;
  for (const section of plan.sections) {
    const sectionTableBinding = tableBindings.find((binding) => binding.sectionId === section.id);
    for (const field of section.fields) {
      if (field.type === "question") {
        const binding = questionBindings.find(
          (candidate) => candidate.fieldId === (field.schema ?? field.uid),
        );
        if (binding && binding.mode !== "manual") fillableFields += 1;
      } else if (field.type === "table" && field.schema) {
        if (tableBindings.some((binding) => binding.sectionId === section.id && binding.schema === field.schema)) {
          fillableFields += 1;
        }
      }
      // "unknown" field types have no writable Autodesk affordance and are never fillable.
    }
    if (sectionTableBinding) {
      for (const column of section.columns) {
        if (sectionTableBinding.columns.some((columnBinding) => columnBinding.columnId === column.uid)) {
          fillableFields += 1;
        }
      }
    }
  }

  return {
    categories: (plan.displayGroups ?? []).length,
    fields: layout.fieldCount,
    fillableFields,
    tables: layout.tableCount,
  };
}
